import moment from "moment-timezone";

const TZ = "Asia/Tokyo";

export const toCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]+/g, ""));

  if (Number.isNaN(numeric)) return "-";

  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(numeric)} JPY`;
};

export const parseNumeric = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]+/g, ""));

  return Number.isNaN(numeric) ? null : numeric;
};

export const normalizeOptionValue = (value) =>
  String(value || "").trim().toLowerCase().replace(/\s+/g, "-");

export const toStatusMeta = (status, responseBy) => {
  const raw = String(status || "").trim().toUpperCase();

  if (raw === "PENDING_APPROVAL") return { label: "Request Pending", type: "pending" };
  if (raw === "APPROVED" && responseBy) return { label: "Request Approved", type: "approved" };
  if (raw === "APPROVED" && !responseBy) return { label: "Updated", type: "updated" };
  if (raw === "REJECTED") return { label: "Request Rejected", type: "rejected" };
  if (raw === "DRAFT") return { label: "Draft", type: "draft" };

  return { label: status || "-", type: "pending" };
};

const getPaymentReceived = (data, role) => {
  if(role === "business") return "-";

  if (data?.status === "APPROVED") {
    if (data?.paymentStatus === "CHARGED") {
      if (data?.previousBooking?.paymentStatus === "CHARGED" || data?.previousBooking?.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") {
        return data?.previousBooking?.totalPrice;
      } else {
        return data?.chargeAmount;
      }
    } else {
      if (data?.previousBooking?.paymentStatus === "CHARGED" || data?.previousBooking?.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") {
        return data?.previousBooking?.totalPrice;
      } else {
        return "-";
      }
    }
  }

  if (data?.bookingId?.paymentStatus === "CHARGED" || data?.bookingId?.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") {
    return data?.bookingId?.totalPrice;
  }

  return "-";
}

export const mapRequestToRow = (item, index) => {
  const statusMeta = toStatusMeta(item?.status, item?.responseBy);
  const requestedAt = item?.requestCreatedAt || item?.date || item?.createdAt || null;
  const requestedMoment = requestedAt ? moment.tz(requestedAt, TZ) : null;
  const adminUpdatedAt = item?.respondedAt || null;
  const adminUpdatedMoment = adminUpdatedAt ? moment.tz(adminUpdatedAt, TZ) : null;

  const holderName =
    item?.bookingId?.customer?.name ||
    item?.bookingId?.passengerName ||
    item?.userId?.name ||
    "-";

  const userRole = String(item?.userId?.role || "").toLowerCase();
  const isDeferred = item?.userId?.deferredPaymentEligible;
  const userTypeLabel = userRole === "individual" ? "B2C" : isDeferred ? "B2B (Deferred)" : "B2B (Advanced)";
  const userTypeValue = userRole === "business" ? "business" : "regular";

  const paymentRaw = getPaymentReceived(item, userRole);
  const newPriceRaw = item?.editBooking?.totalPrice + item?.totalCancellationFee;
  const paymentNumeric = parseNumeric(paymentRaw);
  const newPriceNumeric = parseNumeric(newPriceRaw);

  let trend = "flat";
  if (paymentNumeric !== null && newPriceNumeric !== null) {
    if (newPriceNumeric > paymentNumeric) trend = "up";
    if (newPriceNumeric < paymentNumeric) trend = "down";
  }

  const adminName = item?.responseBy?.name || "-";

  return {
    id: item?._id || `${normalizeOptionValue(holderName)}-${requestedAt || index}`,
    holder: holderName,
    requestedOn: requestedMoment
      ? [requestedMoment.locale("en").format("MMM D, YYYY"), requestedMoment.format("H:mm")]
      : ["-", "-"],
    requestedAt: requestedAt || null,
    status: statusMeta.label,
    statusType: statusMeta.type,
    userType: userTypeLabel,
    userTypeValue,
    paymentReceived: toCurrency(paymentRaw),
    newPrice: toCurrency(newPriceRaw),
    trend,
    adminUpdate: adminUpdatedMoment
      ? [adminUpdatedMoment.locale("en").format("MMM D, YYYY"), adminUpdatedMoment.format("H:mm")]
      : "-",
    admin: adminName,
    adminValue: normalizeOptionValue(adminName),
    requestType: item?.requestType
  };
};

export const buildRequestedDateText = (requestedDateRange) => {
  if (!requestedDateRange) return "";

  return `${moment.tz(requestedDateRange.startDate, TZ)
    .locale("en")
    .format("MMM D, YYYY")} - ${moment.tz(requestedDateRange.endDate, TZ)
      .locale("en")
      .format("MMM D, YYYY")}`;
};

export const getFilterSelectStyles = (hasValue) => ({
  control: (base, state) => ({
    ...base,
    minHeight: "45px",
    height: "45px",
    borderRadius: "5px",
    borderColor: state.isFocused ? "#D9D9D9" : "#D9D9D9",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      borderColor: "#D9D9D9",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 15px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#7C7B7B",
    fontSize: "15px",
    lineHeight: "22px",
  }),
  singleValue: (base) => ({
    ...base,
    color: hasValue ? "#282828" : "#7C7B7B",
    fontSize: "15px",
    lineHeight: "22px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    paddingRight: "15px",
    color: "#282828",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "5px",
    marginTop: "6px",
    zIndex: 30,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
    color: "#282828",
    cursor: "pointer",
    fontSize: "15px",
    lineHeight: "22px",
    "&:hover": {
      backgroundColor: "#F7F7F7",
    },
  }),
});

export const filterRows = ({
  rows,
  search,
  selectedStatus,
  selectedUserType,
  selectedAdmin,
  requestedDateRange,
}) => {
  const keyword = search.trim().toLowerCase();

  return rows.filter((row) => {
    if (keyword) {
      const searchableText = [
        row.holder,
        row.status,
        row.userType,
        row.admin,
        row.newPrice,
        row.paymentReceived,
        row.requestedOn.join(" "),
      ]
        .join(" ")
        .toLowerCase();

      if (!searchableText.includes(keyword)) return false;
    }

    if (selectedStatus && row.statusType !== selectedStatus.value) return false;
    if (selectedUserType && row.userTypeValue !== selectedUserType.value) return false;
    if (selectedAdmin && row.adminValue !== selectedAdmin.value) return false;

    if (requestedDateRange && row.requestedAt) {
      const requestDate = moment(row.requestedAt);
      const start = moment(requestedDateRange.startDate).startOf("day");
      const end = moment(requestedDateRange.endDate).endOf("day");

      if (!requestDate.isBetween(start, end, undefined, "[]")) return false;
    }

    if (requestedDateRange && !row.requestedAt) return false;

    return true;
  });
};
